import {Injectable, Injector} from "@angular/core";
import {Router} from "@angular/router";
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse} from "@angular/common/http";
import {BehaviorSubject, Observable, throwError} from "rxjs";
import {catchError, filter, switchMap, take} from "rxjs/operators";
import {AccessTokenResponse, PerFormaAPIService} from "src/app/services/pfapi.service";

// endpoints that must NOT carry the bearer token (auth bootstrap itself)
const AUTH_ENDPOINTS = ["/api/auth/login", "/api/auth/refresh"];
const TOKEN_KEY = "pf_token";
const REFRESH_KEY = "pf_refresh";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    // guards against firing multiple concurrent refreshes; queued requests wait on the subject below
    private isRefreshing = false;
    private readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);

    // resolve PerFormaAPIService lazily via Injector — injecting it directly would create a cyclic
    // dependency (the API service depends on HttpClient, which builds this interceptor).
    constructor(
        private readonly router: Router,
        private readonly injector: Injector
    ) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const isAuthEndpoint = AUTH_ENDPOINTS.some(url => req.url.includes(url));
        const token = localStorage.getItem(TOKEN_KEY);

        const authReq = token && !isAuthEndpoint ? this.addTokenHeader(req, token) : req;

        return next.handle(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
                // an expired access token surfaces as a 401 on a normal API call — try to refresh
                // and replay the request before giving up and sending the user back to login.
                if (error.status === 401 && !isAuthEndpoint) {
                    return this.handle401Error(authReq, next);
                }
                return throwError(() => error);
            })
        );
    }

    private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (this.isRefreshing) {
            // a refresh is already in flight — wait for the new token, then replay this request
            return this.refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => next.handle(this.addTokenHeader(request, token as string)))
            );
        }

        const refreshToken = localStorage.getItem(REFRESH_KEY);
        if (!refreshToken) {
            return this.forceLogin();
        }

        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        const api = this.injector.get(PerFormaAPIService);
        return api.postApiAuthRefresh({RefreshToken: refreshToken}).pipe(
            switchMap((result: AccessTokenResponse) => {
                this.isRefreshing = false;
                if (!result.AccessToken) {
                    return this.forceLogin();
                }

                localStorage.setItem(TOKEN_KEY, result.AccessToken);
                if (result.RefreshToken) localStorage.setItem(REFRESH_KEY, result.RefreshToken);

                // release any requests queued while the refresh was in flight, then replay this one
                this.refreshTokenSubject.next(result.AccessToken);
                return next.handle(this.addTokenHeader(request, result.AccessToken));
            }),
            catchError(() => {
                // refresh token is expired/invalid — clear session and bounce to login
                this.isRefreshing = false;
                return this.forceLogin();
            })
        );
    }

    private forceLogin(): Observable<never> {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        this.router.navigate(["/account/login"]);
        return throwError(() => new HttpErrorResponse({status: 401, statusText: "Session expired"}));
    }

    private addTokenHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
        return request.clone({setHeaders: {Authorization: `Bearer ${token}`}});
    }
}
